/**
 * Browser-side JSON body compression for direct cell-API (CORS) calls.
 *
 * Always gzips the body with `content-encoding: x-gzip` when CompressionStream
 * is available, so that colored/rich text in payloads is not inspected or
 * stripped by the edge WAF regardless of payload size.  Falls back to plain
 * JSON only when CompressionStream is unavailable or when the compressed form
 * would be larger than the raw body.
 *
 * Matches the downstream XGzipDecompressionMiddleware expected by backend
 * services.
 */
export const BROWSER_WAF_BODY_LIMIT = 8 * 1024;

/** Size diagnostics for an outbound JSON body (see {@link prepareGzipJsonBody}). */
export type GzipJsonBodyMeta = {
  /** UTF-8 byte length of the raw JSON before any compression. */
  rawBytes: number;
  /** Byte length actually sent (gzipped size when compressed, else raw size). */
  sentBytes: number;
  /** True when the body was gzipped (`content-encoding: x-gzip`). */
  gzipped: boolean;
  /** True when the raw JSON exceeded the edge-WAF body limit. */
  exceededWafLimit: boolean;
};

const EMPTY_BODY_META: GzipJsonBodyMeta = {
  rawBytes: 0,
  sentBytes: 0,
  gzipped: false,
  exceededWafLimit: false,
};

export async function prepareGzipJsonBody(body: unknown): Promise<{
  body: BodyInit | undefined;
  extraHeaders: Record<string, string>;
  meta: GzipJsonBodyMeta;
}> {
  if (body === undefined) {
    return { body: undefined, extraHeaders: {}, meta: EMPTY_BODY_META };
  }
  const bodyStr = JSON.stringify(body);
  const byteLen = new Blob([bodyStr]).size;
  const exceededWafLimit = byteLen > BROWSER_WAF_BODY_LIMIT;
  const uncompressedMeta: GzipJsonBodyMeta = {
    rawBytes: byteLen,
    sentBytes: byteLen,
    gzipped: false,
    exceededWafLimit,
  };
  if (typeof CompressionStream === 'undefined') {
    return { body: bodyStr, extraHeaders: {}, meta: uncompressedMeta };
  }
  const stream = new Blob([bodyStr]).stream().pipeThrough(new CompressionStream('gzip'));
  const compressed = new Uint8Array(await new Response(stream).arrayBuffer());
  if (compressed.byteLength >= byteLen) {
    return { body: bodyStr, extraHeaders: {}, meta: uncompressedMeta };
  }
  return {
    body: compressed,
    extraHeaders: { 'content-encoding': 'x-gzip' },
    meta: {
      rawBytes: byteLen,
      sentBytes: compressed.byteLength,
      gzipped: true,
      exceededWafLimit,
    },
  };
}
